import React, { useState } from 'react';
import { Star, X, Truck, ShoppingBag, MessageSquare } from 'lucide-react';
import { Button } from './Button';
import { api } from '../lib/axios';
import toast from 'react-hot-toast';
import { useTranslation } from "react-i18next";

interface OrderReviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    orderId: string;
    onSuccess: () => void;
}

export const OrderReviewModal: React.FC<OrderReviewModalProps> = ({ isOpen, onClose, orderId, onSuccess }) => {
    const { t } = useTranslation();
    const [rating, setRating] = useState(5);
    const [deliveryRating, setDeliveryRating] = useState(5);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await api.post('/ecommerce/reviews', {
                orderId,
                rating,
                deliveryRating,
                comment
            });
            toast.success('Thank you for your feedback!');
            onSuccess();
            onClose();
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to submit review');
        } finally {
            setIsSubmitting(false);
        }
    };

    const StarSelector = ({ value, onChange, label, icon: Icon, colorClass }: any) => (
        <div className="space-y-3">
            <div className="flex items-center gap-2 mb-1">
                <Icon size={16} className={colorClass} />
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
            </div>
            <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                    <button
                        key={star}
                        type="button"
                        onClick={() => onChange(star)}
                        className="transition-transform hover:scale-110 active:scale-95 outline-none"
                    >
                        <Star 
                            size={32}
                            className={`${star <= value ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'}`} 
                        />
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-xl z-[200] flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-[32px] shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300">
                <div className="p-6 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
                    <div>
                        <h2 className="text-xl font-black text-slate-800 tracking-tight">{t('ui_rate_your_purch_8e4cdf', `Rate Your Purchase`)}</h2>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{t('ui_order___d1228f', `Order #`)}{orderId.slice(0, 8).toUpperCase()}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white rounded-full transition-colors shadow-sm">
                        <X size={20} className="text-slate-400" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-8">
                    <div className="space-y-6">
                        <StarSelector 
                            value={rating} 
                            onChange={setRating} 
                            label={t('ui_product_quality_72371d', `Product Quality`)} 
                            icon={ShoppingBag} 
                            colorClass="text-brand-500" 
                        />
                        <div className="h-px bg-slate-100" />
                        <StarSelector 
                            value={deliveryRating} 
                            onChange={setDeliveryRating} 
                            label={t('ui_delivery_servic_5cdd30', `Delivery Service`)} 
                            icon={Truck} 
                            colorClass="text-blue-500" 
                        />
                    </div>

                    <div className="space-y-3">
                        <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            <MessageSquare size={14} />
                            {t('ui_comment__option_3b45c3', `Comment (Optional)`)}</label>
                        <textarea
                            className="w-full rounded-2xl border border-slate-100 bg-slate-50 focus:border-brand-500 focus:ring-4 focus:ring-brand-500/5 text-sm p-4 min-h-[100px] outline-none transition-all resize-none"
                            placeholder={t('ui_tell_us_what_yo_6180b8', `Tell us what you liked or how we can improve...`)}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />
                    </div>

                    <div className="flex gap-3">
                         <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-4 bg-slate-100 text-slate-600 font-bold rounded-2xl hover:bg-slate-200 transition-all text-sm"
                        >
                            {t('ui_later_61057a', `Later`)}</button>
                        <Button
                            fullWidth
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-[2] bg-slate-900 text-white font-bold rounded-2xl shadow-xl shadow-slate-900/20 hover:bg-slate-800 border-transparent h-14"
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Review'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};
